<template>
    <div class="relative">
        <input type="hidden" name="brick_type" :value="sku">

        <div class="absolute w-full h-full top-0 left-0 py-1 -mt-px pl-3 pr-8 pointer-events-none select-none" v-if="selection">
            <div class="flex items-center whitespace-nowrap">
                <div class="w-6 h-6 flex items-center justify-center overflow-hidden rounded-sm mr-3">
                    <div class="w-full h-full bg-center bg-cover" :style="{
                        'background-image': `url(${selection.primaryImage.url})`,
                    }"></div>
                </div>

                <div class="overflow-hidden">
                    <span class="block text-sm font-medium" v-text="selection.display_name"></span>
                    <span class="block text-xs opacity-75" v-text="selection.sku"></span>
                </div>
            </div>
        </div>

        <vue-autosuggest
            ref="autocomplete"
            :class="{ 'has-selection': this.selection }"
            :suggestions="suggestions"
            :inputProps="inputProps"
            :getSuggestionValue="getSuggestionValue"
            autocomplete="off"
            v-model="input"
            @input="search"
            @click="reset"
            @focus="focus"
            @blur="blur"
            @selected="select"
        >
            <div slot-scope="{suggestion}" class="px-2 py-1 cursor-pointer">
                <div class="flex items-center">
                    <div class="w-8 h-8 flex items-center justify-center overflow-hidden rounded-sm mr-2">
                        <div class="w-full h-full bg-center bg-cover" :style="{
                            'background-image': `url(${suggestion.item.primaryImage.url})`,
                        }"></div>
                    </div>

                    <div>
                        <span class="cursor-pointer block text-sm font-medium" v-text="suggestion.item.display_name"></span>
                        <span class="cursor-pointer block text-xs opacity-75" v-text="suggestion.item.sku"></span>
                    </div>
                </div>
            </div>

            <div v-if="! instance && focused && 0 !== (input || '').length && 0 === length" class="autosuggest__results-container" slot="after-input">
                <div class="suggestion-wrapper">
                    <div class="px-3 py-1">
                        <!-- <div v-if="this.instance" class="flex flex-col items-center">
                            <span class="block text-sm font-medium">Searching...</span>
                            <span class="block text-xs opacity-75">Please wait.</span>
                        </div> -->

                        <div>
                            <span class="block text-sm font-medium">No Bricks Found</span>
                            <span class="block text-xs opacity-75">Please try another search term to find a brick</span>
                        </div>
                    </div>
                </div>
            </div>
        </vue-autosuggest>
    </div>
</template>

<script>
import { uniqueId } from 'lodash-es';
import { debounce } from 'lodash-es';

import { VueAutosuggest } from 'vue-autosuggest';

export default {
    components: {
        VueAutosuggest,
    },

    props: {
        value: {
            type: String,
            required: false,
        },
    },

    data() {
        return {
            instance: null,
            suggestions: [],
            input: null,
            selection: null,
            previous: null,
            focused: false,
        };
    },

    mounted() {
        const searchParams = new URLSearchParams(window.location.search);
        if (! searchParams.get('sku')) {
            return;
        }

        this.input = searchParams.get('sku');
        this.autocomplete.value = searchParams.get('sku');

        this.search(this.input, 1)
            .then(({ suggestions, items }) => {
                if (0 === suggestions.length) {
                    return;
                }

                this.selection = items[0];
            });
    },

    watch: {
        selection() {
            Orbit.$emit('global::brick:selected', this.selection);
            this.$emit('selected', this.selection);
        },

        input(e) {
            this.selection = null;
        },
    },

    computed: {
        sku() {
            if (! this.selection) {
                return this.input;
            }

            return this.selection.sku;
        },

        autocomplete() {
            return this.$refs.autocomplete;
        },

        inputProps() {
            return {
                placeholder: 'Search brick names...',
                name: 'brick_type_autocomplete',
                class: 'input w-full',
            };
        },

        variations() {
            return this.selection ? this.selection.variations : [];
        },

        length() {
            return 0 === this.suggestions.length ? 0
                : this.suggestions[0].data.length;
        },
    },

    methods: {
        reset() {
            if (! this.selection) {
                return;
            }

            this.previous = this.selection;
            this.selection = null;
        },

        focus() {
            this.focused = true;
        },

        blur() {
            this.focused = false;

            this.recover();
        },

        recover() {
            if (! this.previous || this.previous.sku !== this.autocomplete.value) {
                return;
            }

            this.selection = this.previous;
        },

        select(selection) {
            this.selection = selection ? selection.item : null;
        },

        search(term, limit = 5) {
            let instance = this.instance = uniqueId('brickLookup');

            return new Promise((resolve, reject) => {
                axios.get('/api/cortex/bricks', {
                    params: {
                        'filter[search]': term,
                        'per_page': limit,
                    },
                }).then(({ data }) => {
                    if (this.instance !== instance) {
                        return;
                    }

                    // If no results from first request, make a second request
                    if (data.data.length === 0) {
                        return axios.get('/api/cortex/bricks/clearance', {
                            params: {
                                'filter[search]': term,
                                'per_page': limit,
                            }
                        })
                    }

                    return data;
                }).then((response) => {
                    if (this.instance !== instance) {
                        return;
                    }

                    this.instance = null;

                    // Handle response based on whether it came from the first or second request
                    const results = response.data.data ? response.data.data : response.data;

                    this.suggestions = [{ data: results }];

                    resolve({ suggestions: this.suggestions, items: results });
                }).catch(reject);
            });
        },

        getSuggestionValue(suggestion) {
            return suggestion.item.sku;
        },
    },
};
</script>

<style lang="scss">
.outline-none {
    outline: none;
}

.has-selection input {
    color: transparent;
}

.autosuggest__results-container {
    left: 0;
    right: 0;
    top: 2.5rem;
    position: absolute;

    .autosuggest__results {
        position: absolute;
        width: 100%;
        left: 0;
        right: 0;
        top: 0;
        z-index: 25;

        &_item-highlighted {
            background-color: #bcdefa;
        }
    }

    ul,
    .suggestion-wrapper {
        @apply p-0 m-0 bg-light rounded-b-md border border-grey-200 border-t-0 ring-2 ring-green-400;
    }
}
</style>

